import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { isType } from '@sorare/core/src/lib/gql';

import { FeaturedMissionCard } from '#src/components/missions/FeaturedMissionCard/index.tsx';
import { PlayMissionsDialog } from '#src/components/missions/PlayMissionsDialog/index.tsx';
import { PlayMissionsTaskRow } from '#src/components/missions/PlayTask/index.tsx';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';

import type { FeaturedPlayTask_taskInterface } from './__generated__/index.graphql.ts';

type Props = {
  task?: FeaturedPlayTask_taskInterface | null;
  refetch?: () => void;
};

export const FeaturedPlayTask = ({ task, refetch }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  if (!task) {
    return null;
  }

  return (
    <>
      <FeaturedMissionCard color="var(--c-arena-primary)">
        <PlayMissionsTaskRow
          task={task}
          onClaim={
            isType(task, 'Task')
              ? () => {
                  onClaim(task);
                  refetch?.();
                }
              : undefined
          }
          loading={loadingTaskId === task.id}
        />
        <Button
          size="medium"
          color="quaternary"
          onClick={() => setDialogOpen(true)}
        >
          <FormattedMessage
            defaultMessage="View all Play Missions"
            description="FeaturedPlayTask - View all Play Missions button"
          />
        </Button>
      </FeaturedMissionCard>
      <PlayMissionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
          refetch?.();
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
          refetch?.();
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
          refetch?.();
        }}
      />
    </>
  );
};

FeaturedPlayTask.fragments = {
  taskInterface: gql`
    fragment FeaturedPlayTask_taskInterface on TaskInterface {
      id
      ...PlayMissionsTaskRow_taskInterface
      ...TaskClaimedDialogs_taskInterface
    }
    ${PlayMissionsTaskRow.fragments.taskInterface}
    ${TaskClaimedDialogs.fragments.taskInterface}
  ` as TypedDocumentNode<FeaturedPlayTask_taskInterface>,
};
